import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  Grid,
  Divider,
  Stack,
  Alert,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatDateTime } from '../../utils/dateFormat';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../utils/permissions';
import { formatCheckListAmount, getCheckStatusColor, getCheckStatusLabel } from '../../utils/checkDisplay';
import { useAsyncAction } from '../../hooks/useAsyncAction';
import { useFeedback } from '../../context/FeedbackContext';
import CashieringCheckDetail from '../../pages/CashieringCheckDetail';
import { getClientTabPath } from '../../utils/navigationReturn';

function ClientChecksTab({
  clientId,
  client,
  active,
  isMobile,
  itemId,
  clientChecks,
  checksLoading,
  fetchClientChecks,
  onChecksUpdated,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userRole, customPermissions, isInternal } = useAuth();
  const { showError } = useFeedback();
  const canManageChecks = isInternal && hasPermission(userRole, 'manage_checks', customPermissions);
  const { loading: createCheckLoading, run: runCreateCheck } = useAsyncAction();

  const [createCheckDialogOpen, setCreateCheckDialogOpen] = useState(false);
  const [newCheckName, setNewCheckName] = useState('');

  useEffect(() => {
    if (!active || !clientId || !canManageChecks || itemId) return;
    fetchClientChecks(clientId);
  }, [active, clientId, fetchClientChecks, canManageChecks, itemId]);

  const getClientName = (user) => `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || t('common.emptyValue');

  const handleCreateCheck = async (name) => {
    await runCreateCheck(async () => {
      try {
        await axios.post('/api/checks', {
          clientUser: clientId,
          name: name || undefined,
        });
        setCreateCheckDialogOpen(false);
        setNewCheckName('');
        await fetchClientChecks(clientId);
        await onChecksUpdated();
      } catch (error) {
        showError(error.response?.data?.error || t('cashiering.failedToCreateCheck'));
      }
    });
  };

  const openCheckDetail = (check) => {
    const ids = clientChecks.map((c) => c._id);
    navigate(getClientTabPath(clientId, 'checks', check._id), {
      state: { ids, index: ids.indexOf(check._id) },
    });
  };

  if (!active) return null;

  if (itemId) {
    return <CashieringCheckDetail embedded checkId={itemId} clientId={clientId} />;
  }

  if (!canManageChecks) {
    return (
      <Alert severity="warning">
        {t('users.noPermission')}
      </Alert>
    );
  }

  return (
    <>
      {checksLoading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress size={32} /></Box>
      ) : (
        <>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => { setNewCheckName(''); setCreateCheckDialogOpen(true); }}>
              {t('clients.createCheck')}
            </Button>
          </Box>
          {clientChecks.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" py={4}>{t('clients.noChecks')}</Typography>
          ) : isMobile ? (
            <Stack spacing={1.5}>
              {clientChecks.map((check) => (
                <Card key={check._id} variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => openCheckDetail(check)}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Typography variant="body1" fontWeight="bold">
                        {check.name || t('cashiering.checkNumber', { id: check._id.slice(-6) })}
                      </Typography>
                      <Chip label={getCheckStatusLabel(check.status, t)} color={getCheckStatusColor(check.status)} size="small" />
                    </Box>
                    <Divider sx={{ mb: 1 }} />
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block">{t('cashiering.items')}</Typography>
                        <Typography variant="body2">{check.itemCount || 0}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block">{t('checks.total')}</Typography>
                        <Typography variant="body2">{formatCheckListAmount(check.totalItems, check)}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block">{t('cashiering.payments')}</Typography>
                        <Typography variant="body2">{formatCheckListAmount(check.totalDeposits, check)}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block">{t('cashiering.amountDue')}</Typography>
                        <Typography variant="body2" fontWeight="bold" color={(check.amountDue || 0) > 0 ? 'error.main' : 'success.main'}>
                          {formatCheckListAmount(check.amountDue, check)}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                      {check.createdAt ? formatDateTime(check.createdAt) : t('common.emptyValue')}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('cashiering.name')}</TableCell>
                    <TableCell>{t('cashiering.checkStatus')}</TableCell>
                    <TableCell>{t('cashiering.items')}</TableCell>
                    <TableCell>{t('checks.total')}</TableCell>
                    <TableCell>{t('cashiering.payments')}</TableCell>
                    <TableCell>{t('cashiering.amountDue')}</TableCell>
                    <TableCell>{t('cashiering.created')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clientChecks.map((check) => (
                    <TableRow key={check._id} hover sx={{ cursor: 'pointer' }} onClick={() => openCheckDetail(check)}>
                      <TableCell><Typography variant="body2" fontWeight="bold">{check.name || t('cashiering.checkNumber', { id: check._id.slice(-6) })}</Typography></TableCell>
                      <TableCell><Chip label={getCheckStatusLabel(check.status, t)} color={getCheckStatusColor(check.status)} size="small" /></TableCell>
                      <TableCell>{check.itemCount || 0}</TableCell>
                      <TableCell>{formatCheckListAmount(check.totalItems, check)}</TableCell>
                      <TableCell>{formatCheckListAmount(check.totalDeposits, check)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" color={(check.amountDue || 0) > 0 ? 'error.main' : 'success.main'}>
                          {formatCheckListAmount(check.amountDue, check)}
                        </Typography>
                      </TableCell>
                      <TableCell><Typography variant="caption">{check.createdAt ? formatDateTime(check.createdAt) : '—'}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Dialog open={createCheckDialogOpen} onClose={() => setCreateCheckDialogOpen(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          {client
            ? t('cashiering.createCheckTitleWithClient', { name: getClientName(client) })
            : t('cashiering.createCheckTitle')}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('cashiering.checkNameOptional')}
            value={newCheckName}
            onChange={(e) => setNewCheckName(e.target.value)}
            margin="normal"
            placeholder={t('cashiering.checkNamePlaceholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateCheckDialogOpen(false)} disabled={createCheckLoading}>{t('cashiering.cancel')}</Button>
          <Button onClick={() => handleCreateCheck(newCheckName)} variant="contained" disabled={createCheckLoading}>
            {createCheckLoading ? t('common.creating') : t('cashiering.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ClientChecksTab;
